import React, { useState } from 'react';
import { AutoComplete, Button, Modal, Typography, Alert, Space, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { apiRoot } from '../../services/apiInstances';
import repairDataLookupApi from '../../services/api/repairDataLookupApi';

const { Text } = Typography;

/**
 * Start a vendor lookup from the estimate or work order.
 *
 * Until this existed there was no way to run a lookup from inside the app at all —
 * every quote the owner reviewed had been produced by a script. This is the button.
 *
 * Free text rather than a dropdown, because the owner asked for search: type "water
 * pump", take the best match, go. What a job means internally is a list of candidate
 * paths through the vendor's tree, which is not something an advisor should ever have to
 * see, let alone type.
 */

interface JobMatch {
  name: string;
  lookupPathCandidates: string[][];
  score?: number;
  exact?: boolean;
  reason?: string;
}

export interface RepairDataLookupButtonProps {
  documentType: 'estimate' | 'work_order';
  documentId: number;
  /** Attach to an existing line. Omit to create a new one. */
  lineId?: number;
  /** Called once the lookup is queued, so the screen can show the queue. */
  onQueued?: () => void;
  block?: boolean;
}

export const RepairDataLookupButton: React.FC<RepairDataLookupButtonProps> = ({
  documentType,
  documentId,
  lineId,
  onQueued,
  block,
}) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [matches, setMatches] = useState<JobMatch[]>([]);
  const [searching, setSearching] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async (q: string) => {
    setTerm(q);
    setError(null);
    if (q.trim().length < 2) {
      setMatches([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await apiRoot.get<{ matches: JobMatch[]; launchDirectly: string | null }>(
        '/procedures/jobs',
        { params: { q } }
      );
      setMatches(data.matches ?? []);
    } catch {
      // A search that cannot run must not look like a job that does not exist.
      setError('Could not search jobs. The lookup service may be unavailable.');
      setMatches([]);
    } finally {
      setSearching(false);
    }
  };

  /**
   * Start a lookup from whatever the advisor gave us.
   *
   * `job` is a suggestion they picked, and carries known paths. When they simply
   * typed, there is no path and the QUERY travels instead — the job resolves it live
   * against this vehicle's own vendor tree and comes back asking if it is not
   * clear. That is what makes "cabin filter", "cam phaser" and "cylinder head"
   * work on a vehicle nobody wrote a list for.
   */
  const start = async (job: JobMatch | { name: string; lookupPathCandidates?: string[][] }) => {
    setStarting(true);
    setError(null);
    try {
      await repairDataLookupApi.start({
        documentType,
        documentId,
        lineId,
        lookupPathCandidates: job.lookupPathCandidates?.length ? job.lookupPathCandidates : undefined,
        query: job.lookupPathCandidates?.length ? undefined : job.name,
        label: job.name,
        // The same press twice must not start two scrapes. There is ONE vendor
        // seat, so a duplicate is not merely wasteful — it is what breaks the seat.
        idempotencyKey: `${documentType}:${documentId}:${lineId ?? 'new'}:${job.name}`,
      });
      message.success(`${job.name} queued`);
      setOpen(false);
      setTerm('');
      setMatches([]);
      onQueued?.();
    } catch (e: any) {
      const body = e?.response?.data?.error;
      // A full queue is not an error to retry — it clears by finishing or by
      // cancelling something, so say that rather than showing a bare failure.
      setError(body?.message || 'Could not queue the lookup.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <Button icon={<SearchOutlined />} onClick={() => setOpen(true)} block={block}>
        Look up in ALLDATA
      </Button>
      <Modal
        title="Look up a job in ALLDATA"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Text type="secondary">
            Type the job — "water pump", "cabin filter", "cylinder head", "cam phaser". It is
            looked up on THIS vehicle's own ALLDATA tree, so anything ALLDATA files for it can
            be found. If the words match more than one thing — "wheel hub" is the front hub or
            the rear axle bearing — it stops and asks before spending the lookup. Runs in the
            background over a few minutes; you can carry on working.
          </Text>
          <AutoComplete
            autoFocus
            style={{ width: '100%' }}
            value={term}
            onChange={search}
            placeholder="Type the job — anything ALLDATA files for this vehicle"
            // Enter looks it up as typed. The suggestions below are shortcuts, not
            // the menu: a shop asks for "cabin filter", "cam phaser" or "cylinder
            // head", and none of those can be on a list that is right for every
            // vehicle — the tree differs by year, make, model and engine. What is
            // typed is resolved against THIS vehicle's tree, live.
            onKeyDown={(e) => {
              if (e.key === 'Enter' && term.trim().length >= 2 && !starting) {
                e.preventDefault();
                start({ name: term.trim() });
              }
            }}
            notFoundContent={
              searching
                ? 'Searching…'
                : term.trim().length >= 2
                  ? 'No shortcut for that — press Enter to look it up on this vehicle'
                  : null
            }
            options={matches.map((m) => ({
              value: m.name,
              label: (
                <div>
                  <div>{m.name}</div>
                  {m.reason && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {m.reason}
                    </Text>
                  )}
                </div>
              ),
            }))}
            onSelect={(value) => {
              const picked = matches.find((m) => m.name === value);
              if (picked) start(picked);
            }}
          />
          {error && <Alert type="warning" showIcon message={error} />}
          {starting && <Text type="secondary">Queueing…</Text>}
          <Text type="secondary" style={{ fontSize: 11 }}>
            Nothing is added to the ticket automatically. The lookup builds the line and its
            parts; recommendations are offered separately and cost nothing until you add them.
          </Text>
        </Space>
      </Modal>
    </>
  );
};

export default RepairDataLookupButton;
